import { useRef, useState } from 'react';
import type { FC } from 'react';

import { Placeholder, useCms, useContent, useRendering, useSetting } from '@eneco/cms';
import { Recaptcha } from '@eneco/components/Recaptcha/Recaptcha';
import type { RecaptchaRef } from '@eneco/components/Recaptcha/Recaptcha';
import { RecaptchaForm } from '@eneco/components/RecaptchaForm/RecaptchaForm';
import RichText from '@eneco/components/RichText/RichText';
import { useProductsPublicSubmitBusinessLead } from '@eneco/dc/hooks';
import { typedYupResolver } from '@eneco/hooks/form';
import { Box, Button, InputText, Stack, Stretch, Text, TextLink, TransitionOpacity } from '@eneco/sparky';
import { FormProvider, useForm } from 'react-hook-form';
import type { SubmitHandler } from 'react-hook-form';
import { object, string } from 'yup';

import { useFormField } from './generic-form/useFormField';
import type { RegisterToNewsletterFormContent } from './register-to-newsletter-form-content.types';
import { NEWSLETTER_FORM_SLOTS, resolveSlot } from './slots';

const ROLE = 'emailAddressFormField';

/** Legacy fallbacks, kept verbatim so an unfilled descriptor reads the same on both lanes. */
const FALLBACK_LABEL = 'Email';
const FALLBACK_PLACEHOLDER = 'mijn@email.nl';

interface NewsletterFormValues {
  emailAddress: string;
  recaptchaToken: string;
  [key: string]: string;
}

const newsletterFormSchema = object({
  emailAddress: string().email().required(),
  recaptchaToken: string().required(),
});

/**
 * `RegisterToNewsletterForm` block for the CMS-agnostic lane, serving **both** providers.
 *
 * NOT shared with `RevolutionRegisterToNewsletterForm`, despite the two declaring the same four
 * fields: that sibling posts a Revolution CRM payload to a different Digital Core endpoint
 * (`useFormsPublicV2PostB2BForm` vs `useProductsPublicSubmitBusinessLead`) under a different field
 * name (`emailaddress1` vs `emailAddress`), and it requires a `formId` that only a hidden-field block
 * supplies. Aliasing the two would submit to the wrong endpoint.
 *
 * Mirrors `libs/sitecore/components/forms/src/RegisterToNewsletterForm/RegisterToNewsletterForm.tsx`:
 * an email input beside a submit button (stacked when `displayColumn` is set), then the disclaimer
 * and the reCAPTCHA notice; on success or failure the form is replaced by the matching placeholder
 * and a "back" link that resets it.
 *
 * The email descriptor (`label`/`placeholder`/`hint`/`requiredMessage`/`validationMessage`) is the
 * `formFields` family entry with `role === 'emailAddressFormField'`, read with the same `useFormField`
 * helper the `GenericForm*` fields use.
 *
 * Known simplification: the legacy component reports form funnel events through `useTracking`
 * (`trackFormInteractionStart`/`trackFormInteractionCompleted`) and identifies the submitter to
 * Microsoft Customer Insights through `authenticateUser` (`@eneco/customer-insights`). Neither
 * `lib:tracking` nor `lib:customer-insights` is in `lib:cms-components`'s allowed dependencies
 * (`eslint.config.mjs`), and both are fire-and-forget analytics side effects with no bearing on what
 * renders or on what is submitted — so both are dropped, the same trade `TopTaskCTA` makes. The lead
 * itself still reaches the Digital Core unchanged.
 *
 * Known simplification: the legacy component also accepts a `setFormState` *function* through its
 * rendering params, which a parent flow passes to mirror the form's state into its own chrome. Params
 * cross the CMS boundary as strings (`useSetting`), and this lane has no such parent, so that
 * callback is dropped; the legacy component already treats it as optional.
 *
 * Known simplification: the three `jss-newsletter-form-*` placeholders have no Sanity-side field, so
 * the intro, success and error copy render on the Sitecore lane only. The form itself submits
 * identically on both.
 */
const RegisterToNewsletterForm: FC = () => {
  const { adapter } = useCms();
  const content = useContent<RegisterToNewsletterFormContent>();
  const rendering = useRendering();
  const displayColumn = useSetting('displayColumn') === 'true';

  const emailField = useFormField('formFields', ROLE);
  const recaptchaRef = useRef<RecaptchaRef>(null);
  const [isTransitioning, setTransitioning] = useState(false);
  const { send, reset, isError, isSuccess } = useProductsPublicSubmitBusinessLead();

  const methods = useForm<NewsletterFormValues>({
    mode: 'onBlur',
    resolver: typedYupResolver<NewsletterFormValues>(newsletterFormSchema),
  });
  const {
    formState: { errors },
    register,
  } = methods;

  const introSlot = resolveSlot(rendering, NEWSLETTER_FORM_SLOTS.intro);
  const successSlot = resolveSlot(rendering, NEWSLETTER_FORM_SLOTS.success);
  const errorSlot = resolveSlot(rendering, NEWSLETTER_FORM_SLOTS.error);

  const disclaimer = adapter.toRichText(content.disclaimerContent);
  const hint = adapter.toRichText(emailField.hint);

  const submitForm: SubmitHandler<NewsletterFormValues> = async values => {
    setTransitioning(true);
    await send({ ...values, recaptchaEnterpriseToken: values.recaptchaToken });
    setTimeout(() => setTransitioning(false), 500);
  };

  // The author's own messages win over yup's defaults, per error type — kept as the legacy component has it.
  const emailErrorMessage = (): string | undefined => {
    const fallback = errors.emailAddress?.message;
    if (errors.emailAddress?.type === 'required') return emailField.requiredMessage ?? fallback;
    if (errors.emailAddress?.type === 'email') return emailField.validationMessage ?? fallback;
    return undefined;
  };

  return (
    <TransitionOpacity in={!isTransitioning}>
      <Stack gap="6">
        {!isSuccess && !isError && (
          <>
            {introSlot ? <Placeholder name={introSlot} /> : null}
            <FormProvider {...methods}>
              <RecaptchaForm onSubmit={submitForm} recaptchaRef={recaptchaRef}>
                <Stack gap="6">
                  <Stack
                    direction={displayColumn ? 'column' : { initial: 'column', md: 'row' }}
                    gap="6"
                    alignY="justify"
                    alignX={{ initial: undefined, md: 'justify' }}>
                    <Stack.Item grow>
                      <InputText
                        {...register('emailAddress')}
                        label={emailField.label ?? FALLBACK_LABEL}
                        placeholder={emailField.placeholder ?? FALLBACK_PLACEHOLDER}
                        hint={hint ? <RichText html={hint} /> : undefined}
                        error={emailErrorMessage()}
                      />
                    </Stack.Item>
                    {/** Box padding hack to prevent jumping when there's a validation error */}
                    <Box paddingTop={displayColumn ? '0' : { md: '7' }}>
                      <Stretch width={{ initial: true, md: false }} height={false}>
                        <Button size="compact" type="submit">
                          {content.nextButtonText}
                        </Button>
                      </Stretch>
                    </Box>
                  </Stack>
                  {disclaimer && (
                    <Text size="BodyS">
                      <RichText html={disclaimer} />
                    </Text>
                  )}
                  <Text size="BodyS">
                    <Recaptcha hasError={!!errors.recaptchaToken} ref={recaptchaRef} />
                  </Text>
                </Stack>
              </RecaptchaForm>
            </FormProvider>
          </>
        )}

        {isSuccess && successSlot ? <Placeholder name={successSlot} /> : null}
        {isError && errorSlot ? <Placeholder name={errorSlot} /> : null}

        {(isSuccess || isError) && (
          <TextLink
            type="button"
            onClick={() => {
              setTransitioning(true);
              setTimeout(() => {
                reset();
                setTransitioning(false);
              }, 500);
            }}
            emphasis="high">
            {content.backButtonText}
          </TextLink>
        )}
      </Stack>
    </TransitionOpacity>
  );
};

export default RegisterToNewsletterForm;
